<script setup>
// Hedefleme sekmesi: arama odakli calisir.
//
// Meta'nin hedefleme sozlugu cok buyuk oldugu icin liste halinde cekilmez;
// kullanici yazar, yazdigi metin secili turun ads_search_* aracina gider.
// Secilen kalemler bir hedefleme spec'ine cevrilip kitle buyuklugu tahmini ve
// okunur ozet icin kullanilir.
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { aracCalistir, diziyeCevir, sayiBicimle } from './kopru.js';
import AracTablosu from './AracTablosu.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  hesap: { type: String, default: '' },
  hesapAyrinti: { type: Object, default: () => ({}) },
});

const { t } = useI18n();
const route = useRoute();

// Her tur kendi arama aracina baglidir; tek arama kutusu bunlar arasinda gecer.
const TURLER = [
  { deger: 'ilgi', arac: 'ads_search_interests', etiket: 'TUR.ILGI' },
  { deger: 'davranis', arac: 'ads_search_behaviors', etiket: 'TUR.DAVRANIS' },
  { deger: 'demografi', arac: 'ads_search_demographics', etiket: 'TUR.DEMOGRAFI' },
  { deger: 'konum', arac: 'ads_search_geo_locations', etiket: 'TUR.KONUM' },
];

// Meta konum anahtarlarinin cogulu duzenli degil (city -> cities); esleme sart.
const KONUM_ANAHTAR = {
  country: 'countries',
  country_group: 'country_groups',
  region: 'regions',
  city: 'cities',
  zip: 'zips',
  geo_market: 'geo_markets',
  place: 'places',
  subcity: 'subcities',
  neighborhood: 'neighborhoods',
  electoral_district: 'electoral_districts',
};

const arama = ref('');
const tur = ref('ilgi');
const sonuclar = ref([]);
const oneriler = ref([]);
const secilenler = ref([]);
const yukleniyor = ref(false);
const islemde = ref(false);
const hata = ref('');
const tahmin = ref(null);
// Aciklama artik ham metin degil; { ozet, veri } tutup okunur satirlara ceviriyoruz.
const aciklama = ref(null);

const accountId = computed(() => route.params.accountId);

const aktifArac = computed(
  () => TURLER.find(x => x.deger === tur.value)?.arac || 'ads_search_interests'
);

const kimlik = (kayit, kalemTuru) => `${kalemTuru}:${kayit.id ?? kayit.key ?? kayit.name}`;

const secili = (kayit, kalemTuru) =>
  secilenler.value.some(s => s.anahtar === kimlik(kayit, kalemTuru));

// Meta yeni surumlerde alt/ust sinir, eskilerde tek audience_size dondurur.
const kitleMetni = kayit => {
  const alt = kayit?.audience_size_lower_bound;
  const ust = kayit?.audience_size_upper_bound;
  if (alt != null && ust != null) return `${sayiBicimle(alt)} - ${sayiBicimle(ust)}`;
  const tek = kayit?.audience_size ?? alt ?? ust;
  return tek == null ? '—' : sayiBicimle(tek);
};

const sutunlar = computed(() => [
  { anahtar: 'name', baslik: t('META_ADS.HEDEF_KITLELER.HEDEFLEME.SUTUN.AD') },
  {
    anahtar: 'type',
    baslik: t('META_ADS.HEDEF_KITLELER.HEDEFLEME.SUTUN.TUR'),
    bicim: (deger, satir) => deger || satir.category || satir.country_code || '—',
  },
  {
    anahtar: 'path',
    baslik: t('META_ADS.HEDEF_KITLELER.HEDEFLEME.SUTUN.YOL'),
    bicim: deger => (Array.isArray(deger) ? deger.join(' › ') : deger || '—'),
  },
  {
    anahtar: 'audience_size_lower_bound',
    baslik: t('META_ADS.HEDEF_KITLELER.HEDEFLEME.SUTUN.KITLE'),
    bicim: (_deger, satir) => kitleMetni(satir),
  },
]);

const ara = async () => {
  // Bos arama Meta'da tum sozlugu tarar; bos iken hic istek atmiyoruz.
  if (!arama.value.trim()) {
    hata.value = t('META_ADS.HEDEF_KITLELER.HEDEFLEME.BOS_ARAMA');
    return;
  }
  yukleniyor.value = true;
  hata.value = '';
  oneriler.value = [];
  try {
    const sonuc = await aracCalistir(accountId.value, aktifArac.value, {
      query: arama.value.trim(),
    });
    sonuclar.value = diziyeCevir(sonuc);
  } catch (e) {
    hata.value = e.response?.data?.error || e.message;
    sonuclar.value = [];
  } finally {
    yukleniyor.value = false;
  }
};

const ekle = (kayit, kalemTuru = tur.value) => {
  const anahtar = kimlik(kayit, kalemTuru);
  if (secilenler.value.some(s => s.anahtar === anahtar)) return;
  secilenler.value.push({ anahtar, tur: kalemTuru, kayit });
  // Secim degisince onceki tahmin gecersiz kalir, yaniltmasin diye temizlenir.
  tahmin.value = null;
  aciklama.value = null;
};

const cikar = anahtar => {
  secilenler.value = secilenler.value.filter(s => s.anahtar !== anahtar);
  tahmin.value = null;
  aciklama.value = null;
};

const temizle = () => {
  secilenler.value = [];
  tahmin.value = null;
  aciklama.value = null;
};

const secilenIlgiler = computed(() => secilenler.value.filter(s => s.tur === 'ilgi'));

// Secilen kalemleri Meta'nin hedefleme spec bicimine cevirir.
const hedeflemeSpec = computed(() => {
  const spec = {};
  const konum = {};
  secilenler.value.forEach(({ tur: kalemTuru, kayit }) => {
    const kalem = { id: String(kayit.id ?? kayit.key ?? ''), name: kayit.name };
    if (kalemTuru === 'ilgi') {
      spec.interests = spec.interests || [];
      spec.interests.push(kalem);
    } else if (kalemTuru === 'davranis') {
      spec.behaviors = spec.behaviors || [];
      spec.behaviors.push(kalem);
    } else if (kalemTuru === 'demografi') {
      // Demografi sonucu kendi Meta anahtarini "type" alaninda tasir
      // (life_events, education_statuses, income...); hepsi tek listeye konamaz.
      const anahtar = kayit.type || 'life_events';
      spec[anahtar] = spec[anahtar] || [];
      spec[anahtar].push(kalem);
    } else if (kalemTuru === 'konum') {
      const tip = kayit.type || 'country';
      if (tip === 'country') {
        konum.countries = konum.countries || [];
        konum.countries.push(kayit.key || kayit.country_code || kayit.id);
      } else {
        const anahtar = KONUM_ANAHTAR[tip] || `${tip}s`;
        konum[anahtar] = konum[anahtar] || [];
        konum[anahtar].push({ key: String(kayit.key ?? kayit.id) });
      }
    }
  });
  if (Object.keys(konum).length) spec.geo_locations = konum;
  return spec;
});

const onerileriGetir = async () => {
  if (!secilenIlgiler.value.length) return;
  islemde.value = true;
  hata.value = '';
  try {
    const sonuc = await aracCalistir(accountId.value, 'ads_get_interest_suggestions', {
      interest_list: secilenIlgiler.value.map(s => s.kayit.name),
    });
    oneriler.value = diziyeCevir(sonuc);
  } catch (e) {
    hata.value = e.response?.data?.error || e.message;
  } finally {
    islemde.value = false;
  }
};

const tahminEt = async () => {
  if (!props.hesap || !secilenler.value.length) return;
  islemde.value = true;
  hata.value = '';
  tahmin.value = null;
  try {
    const sonuc = await aracCalistir(accountId.value, 'ads_estimate_audience_size', {
      account_id: props.hesap,
      targeting: hedeflemeSpec.value,
    });
    const veri = sonuc.veri;
    // Yanit kimi surumde dizi, kimi surumde { data: [...] } ya da duz nesnedir.
    const kok = (Array.isArray(veri) ? veri[0] : veri?.data?.[0] || veri) || {};
    tahmin.value = {
      alt: kok.users_lower_bound ?? kok.estimate_mau_lower_bound ?? kok.estimate_mau ?? kok.users,
      ust: kok.users_upper_bound ?? kok.estimate_mau_upper_bound ?? null,
      ozet: sonuc.ozet,
    };
  } catch (e) {
    hata.value = e.response?.data?.error || e.message;
  } finally {
    islemde.value = false;
  }
};

const aciklamaGetir = async () => {
  // Arac account_id ile cagriliyor; hesap secili degilken bos kimlik gonderip
  // sunucudan hata almak yerine istek hic atilmiyor.
  if (!props.hesap || !secilenler.value.length) return;
  islemde.value = true;
  hata.value = '';
  try {
    const sonuc = await aracCalistir(accountId.value, 'ads_get_targeting_description', {
      account_id: props.hesap,
      targeting: hedeflemeSpec.value,
    });
    // Ham JSON basmak yerine veriyi sakliyoruz; gosterimi aciklamaSatirlari yapar.
    aciklama.value = { ozet: sonuc.ozet || '', veri: sonuc.veri };
  } catch (e) {
    hata.value = e.response?.data?.error || e.message;
  } finally {
    islemde.value = false;
  }
};

// --- Hedefleme ozetinin okunur gosterimi --------------------------------
// Meta'nin spec anahtarlari (locales, geo_locations...) kullaniciya bir sey
// anlatmaz; listede olmayan anahtar oldugu gibi kalir, uydurma etiket yok.
const ALAN_ETIKET = {
  interests: 'ILGILER',
  behaviors: 'DAVRANISLAR',
  geo_locations: 'KONUMLAR',
  excluded_geo_locations: 'HARIC_KONUMLAR',
  countries: 'ULKELER',
  country_groups: 'ULKE_GRUPLARI',
  regions: 'BOLGELER',
  cities: 'SEHIRLER',
  zips: 'POSTA_KODLARI',
  places: 'YERLER',
  age_min: 'YAS_ALT',
  age_max: 'YAS_UST',
  genders: 'CINSIYET',
  locales: 'DILLER',
  life_events: 'YASAM_OLAYLARI',
  education_statuses: 'EGITIM',
  relationship_statuses: 'ILISKI_DURUMU',
  industries: 'SEKTORLER',
  income: 'GELIR',
  custom_audiences: 'OZEL_KITLELER',
  excluded_custom_audiences: 'HARIC_KITLELER',
  publisher_platforms: 'PLATFORMLAR',
  facebook_positions: 'YERLESIMLER',
  instagram_positions: 'INSTAGRAM_YERLESIM',
  device_platforms: 'CIHAZLAR',
};

const etiketle = anahtar => {
  const sonEk = ALAN_ETIKET[anahtar];
  return sonEk ? t(`META_ADS.HEDEF_KITLELER.HEDEFLEME.ALAN.${sonEk}`) : anahtar;
};

// Ic ice nesne ve diziler ham JSON'a donusmesin diye tek satirda ozetlenir.
const degerMetni = deger => {
  if (deger === null || deger === undefined || deger === '') return '';
  if (typeof deger === 'boolean') {
    return deger
      ? t('META_ADS.HEDEF_KITLELER.HEDEFLEME.EVET')
      : t('META_ADS.HEDEF_KITLELER.HEDEFLEME.HAYIR');
  }
  if (Array.isArray(deger)) {
    if (!deger.length) return '';
    // Kalemler cogu zaman { name } tasir; ad varsa onu, yoksa duz degeri yaz.
    const adlar = deger
      .map(oge => (oge && typeof oge === 'object' ? oge.name || oge.key || oge.id : oge))
      .filter(x => x !== null && x !== undefined && x !== '');
    if (adlar.length && adlar.length <= 8 && adlar.every(x => typeof x !== 'object')) {
      return adlar.join(', ');
    }
    return t('META_ADS.HEDEF_KITLELER.HEDEFLEME.OGE', { sayi: deger.length });
  }
  if (typeof deger === 'object') {
    if (deger.name) return String(deger.name);
    const girdiler = Object.entries(deger).filter(
      ([, d]) => d !== null && d !== '' && typeof d !== 'object'
    );
    if (girdiler.length && girdiler.length <= 4) {
      return girdiler.map(([a, d]) => `${etiketle(a)}: ${d}`).join(' · ');
    }
    return t('META_ADS.HEDEF_KITLELER.HEDEFLEME.OGE', { sayi: Object.keys(deger).length });
  }
  return String(deger);
};

// Meta aciklamayi "targetingsentencelines" olarak dondurur: her satirda bir
// baslik (content) ve altinda degerler (children) bulunur. Bu bicim zaten
// okunur; yoksa duz alan/deger listesine dusuyoruz.
const aciklamaSatirlari = computed(() => {
  const veri = aciklama.value?.veri;
  const kok = (Array.isArray(veri) ? veri[0] : veri?.data?.[0] || veri) || null;
  if (!kok || typeof kok !== 'object') return [];
  const cumleler = kok.targetingsentencelines || kok.targeting_sentence_lines;
  if (Array.isArray(cumleler)) {
    return cumleler
      .map((satir, sira) => ({
        anahtar: `satir-${sira}`,
        etiket: satir?.content || '',
        deger: degerMetni(satir?.children),
      }))
      .filter(satir => satir.etiket || satir.deger);
  }
  return Object.entries(kok)
    .map(([anahtar, deger]) => ({
      anahtar,
      etiket: etiketle(anahtar),
      deger: degerMetni(deger),
    }))
    .filter(satir => satir.deger !== '');
});
</script>

<template>
  <div class="p-6">
    <!-- Arama satiri -->
    <div class="flex flex-wrap items-end gap-2 mb-4">
      <label class="text-sm text-n-slate-11">
        {{ t('META_ADS.HEDEF_KITLELER.HEDEFLEME.TUR_BASLIK') }}
        <select v-model="tur" class="!mb-0 !w-auto min-w-[160px]">
          <option v-for="x in TURLER" :key="x.deger" :value="x.deger">
            {{ t(`META_ADS.HEDEF_KITLELER.HEDEFLEME.${x.etiket}`) }}
          </option>
        </select>
      </label>
      <label class="text-sm grow text-n-slate-11">
        {{ t('META_ADS.HEDEF_KITLELER.HEDEFLEME.ARAMA_BASLIK') }}
        <input
          v-model="arama"
          type="text"
          class="!mb-0"
          :placeholder="t('META_ADS.HEDEF_KITLELER.HEDEFLEME.ARAMA_YER_TUTUCU')"
          @keyup.enter="ara"
        />
      </label>
      <Button
        size="sm"
        icon="i-lucide-search"
        :label="t('META_ADS.HEDEF_KITLELER.HEDEFLEME.ARA')"
        :disabled="yukleniyor || !arama.trim()"
        @click="ara"
      />
    </div>

    <p v-if="hata" class="mb-3 text-sm text-n-ruby-11">{{ hata }}</p>

    <!-- Secilen kalemler -->
    <div v-if="secilenler.length" class="p-4 mb-4 border rounded-lg border-n-weak">
      <div class="flex items-center justify-between mb-2">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ t('META_ADS.HEDEF_KITLELER.HEDEFLEME.SECILENLER') }}
        </h3>
        <Button
          variant="faded"
          size="sm"
          :label="t('META_ADS.HEDEF_KITLELER.HEDEFLEME.TEMIZLE')"
          @click="temizle"
        />
      </div>

      <div class="flex flex-wrap gap-2 mb-3">
        <span
          v-for="s in secilenler"
          :key="s.anahtar"
          class="inline-flex items-center gap-2 px-2 py-1 text-xs rounded bg-n-alpha-1 text-n-slate-12"
        >
          {{ s.kayit.name }}
          <button type="button" class="text-n-slate-11" @click="cikar(s.anahtar)">×</button>
        </span>
      </div>

      <div class="flex flex-wrap gap-2">
        <Button
          size="sm"
          icon="i-lucide-gauge"
          :label="t('META_ADS.HEDEF_KITLELER.HEDEFLEME.TAHMIN_ET')"
          :disabled="islemde || !hesap"
          @click="tahminEt"
        />
        <Button
          variant="faded"
          size="sm"
          icon="i-lucide-file-text"
          :label="t('META_ADS.HEDEF_KITLELER.HEDEFLEME.ACIKLAMA_GETIR')"
          :disabled="islemde || !hesap"
          @click="aciklamaGetir"
        />
        <Button
          variant="faded"
          size="sm"
          icon="i-lucide-sparkles"
          :label="t('META_ADS.HEDEF_KITLELER.HEDEFLEME.ONERILER')"
          :disabled="islemde || !secilenIlgiler.length"
          @click="onerileriGetir"
        />
      </div>

      <p class="mt-2 text-xs text-n-slate-11">
        {{ t('META_ADS.HEDEF_KITLELER.HEDEFLEME.TAHMIN_IPUCU') }}
      </p>

      <div v-if="tahmin" class="p-3 mt-3 text-sm rounded bg-n-alpha-1">
        <p class="text-n-slate-11">
          {{ t('META_ADS.HEDEF_KITLELER.HEDEFLEME.TAHMIN_BASLIK') }}
        </p>
        <p v-if="tahmin.alt != null" class="text-base font-medium text-n-slate-12">
          {{ sayiBicimle(tahmin.alt) }}
          <span v-if="tahmin.ust != null"> - {{ sayiBicimle(tahmin.ust) }}</span>
        </p>
        <p v-else class="text-n-slate-12">
          {{ tahmin.ozet || t('META_ADS.HEDEF_KITLELER.HEDEFLEME.TAHMIN_YOK') }}
        </p>
      </div>

      <!-- Ham JSON yerine baslik/deger satirlari: hedeflemenin kime ulastigi
           ancak bu bicimde anlasilir. -->
      <div v-if="aciklama" class="p-3 mt-3 overflow-auto rounded max-h-60 bg-n-alpha-1">
        <p class="mb-2 text-xs text-n-slate-11">
          {{ t('META_ADS.HEDEF_KITLELER.HEDEFLEME.ACIKLAMA_BASLIK') }}
        </p>
        <p v-if="aciklama.ozet" class="mb-2 text-sm whitespace-pre-line text-n-slate-12">
          {{ aciklama.ozet }}
        </p>
        <dl
          v-if="aciklamaSatirlari.length"
          class="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm"
        >
          <template v-for="satir in aciklamaSatirlari" :key="satir.anahtar">
            <dt class="text-n-slate-11">{{ satir.etiket }}</dt>
            <dd class="break-words text-n-slate-12">{{ satir.deger }}</dd>
          </template>
        </dl>
        <p v-else-if="!aciklama.ozet" class="text-sm text-n-slate-11">
          {{ t('META_ADS.HEDEF_KITLELER.HEDEFLEME.ACIKLAMA_BOS') }}
        </p>
      </div>
    </div>

    <!-- Arama sonuclari -->
    <AracTablosu
      :satirlar="sonuclar"
      :sutunlar="sutunlar"
      :yukleniyor="yukleniyor"
      :bos-metin="t('META_ADS.HEDEF_KITLELER.HEDEFLEME.SONUC_YOK')"
    >
      <template #eylemler="{ satir }">
        <Button
          variant="faded"
          size="sm"
          icon="i-lucide-plus"
          :label="
            secili(satir, tur)
              ? t('META_ADS.HEDEF_KITLELER.HEDEFLEME.EKLI')
              : t('META_ADS.HEDEF_KITLELER.HEDEFLEME.EKLE')
          "
          :disabled="secili(satir, tur)"
          @click="ekle(satir)"
        />
      </template>
    </AracTablosu>

    <!-- Ilgi alani onerileri: secilen ilgi alanlarina benzeyenler -->
    <div v-if="oneriler.length" class="mt-6">
      <h3 class="mb-2 text-sm font-medium text-n-slate-12">
        {{ t('META_ADS.HEDEF_KITLELER.HEDEFLEME.ONERILER_BASLIK') }}
      </h3>
      <AracTablosu
        :satirlar="oneriler"
        :sutunlar="sutunlar"
        :bos-metin="t('META_ADS.HEDEF_KITLELER.HEDEFLEME.SONUC_YOK')"
      >
        <template #eylemler="{ satir }">
          <Button
            variant="faded"
            size="sm"
            icon="i-lucide-plus"
            :label="
              secili(satir, 'ilgi')
                ? t('META_ADS.HEDEF_KITLELER.HEDEFLEME.EKLI')
                : t('META_ADS.HEDEF_KITLELER.HEDEFLEME.EKLE')
            "
            :disabled="secili(satir, 'ilgi')"
            @click="ekle(satir, 'ilgi')"
          />
        </template>
      </AracTablosu>
    </div>
  </div>
</template>
